import moment from 'moment';

/* ── Lưới cụm một phút ─────────────────────────────────────────────────────
 * Các kênh của một thiết bị chụp cùng một giây, nên sort thuần theo thời gian
 * để lại thứ tự không ổn định và vị trí camera nhảy giữa các cụm. Ở đây ảnh
 * được gom thành cụm một phút, mỗi cụm có số ô cố định bằng số kênh, ô thứ k
 * luôn là camera k.
 */

// Mã kênh trong payload là chuỗi 3 ký tự 101/201/301/401 — ký tự đầu là số
// camera, hai ký tự cuối luôn là 01. Trả null khi không nhận ra, để ảnh rơi
// vào nhóm "Không rõ kênh" thay vì bị nhét nhầm cột.
export function channelOf(image) {
  const raw = (image?.camera ?? image?.camera_num ?? '').toString().trim();
  if (!raw) return null;
  const m = raw.match(/CAM(\d+)/i);
  if (m) return Number(m[1]);
  if (raw.length === 3 && raw.endsWith('01')) return Number(raw.charAt(0));
  if (/^[0-9]+$/.test(raw)) return Number(raw);
  return null;
}

// `capture_time` là UTC; hai trường kia chỉ là fallback khi thiếu.
export function captureTsOf(image) {
  if (image?.capture_time) return moment.utc(image.capture_time).valueOf();
  if (image?.created_at) return moment(image.created_at).valueOf();
  if (image?.device_time) return moment.unix(image.device_time).valueOf();
  return 0;
}

// Giờ hiển thị cố định UTC+7 — giống hệt cách `displayTime` đang dựng, nên
// nhãn giờ không đổi theo múi giờ của trình duyệt.
const dspHour = ts => moment.utc(ts).add(7, 'hours').format('HH');
const dspHourMinute = ts => moment.utc(ts).add(7, 'hours').format('HH:mm');

// Mốc neo trượt, KHÔNG dùng floor(ts / CLUSTER_MS): cụm vắt qua ranh giới phút
// (07:59:59 + 08:00:01) phải nằm chung một hàng.
export const CLUSTER_MS = 60000;
export function clusterByMinute(imagesAsc) {
  const out = [];
  let cur = null;
  for (const img of imagesAsc) {
    if (!cur || img.ts - cur.anchorTs > CLUSTER_MS) {
      cur = { anchorTs: img.ts, items: [] };
      out.push(cur);
    }
    cur.items.push(img);
  }
  return out;
}

// Kênh thiếu trả placeholder chứ không bỏ ô, nếu không các ô còn lại dồn sang
// trái và cột mất ý nghĩa. Trùng kênh trong cùng cụm thì giữ ảnh mới nhất.
export function toSlots(cluster, channels) {
  const byCh = new Map();
  for (const img of cluster.items) {
    const prev = byCh.get(img.channel);
    if (!prev || img.ts > prev.ts) byCh.set(img.channel, img);
  }
  return channels.map(ch => ({ channel: ch, image: byCh.get(ch) || null }));
}

// Cụm thuộc về giờ của MỐC NEO, kể cả khi có ảnh tràn sang giờ kế.
export function buildHourGroups(images, channels) {
  const groups = {};
  const bucket = hour => {
    if (!groups[hour]) groups[hour] = { clusters: [], unknown: [] };
    return groups[hour];
  };

  const clusterable = [];
  const unknown = [];
  images.forEach(img => {
    if (img.channel != null && img.ts > 0) clusterable.push(img);
    else unknown.push(img);
  });

  clusterable.sort((a, b) => a.ts - b.ts);
  clusterByMinute(clusterable).forEach(cluster => {
    bucket(dspHour(cluster.anchorTs)).clusters.push({
      anchorTs: cluster.anchorTs,
      label: dspHourMinute(cluster.anchorTs),
      slots: toSlots(cluster, channels)
    });
  });

  unknown.forEach(img => {
    bucket(img.ts > 0 ? dspHour(img.ts) : '--').unknown.push(img);
  });

  Object.keys(groups).forEach(hour => {
    groups[hour].clusters.sort((a, b) => b.anchorTs - a.anchorTs);
  });
  return groups;
}
